import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Backdrop,
  Autocomplete,
  Chip,
  useTheme,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import { openSnackbar } from "api/snackbar";
import { SnackbarProps } from "types/snackbar";
import { Formik, Form, Field } from "formik";
import ImageUploader from "components/ImageUploader";
import { GUESTAXIOS } from "../../../../../api/GuestAxios";
import { debounce } from "lodash";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import MainCard from "../../../../../components/MainCard";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { City, Country } from "../../../../../types/Country";
import { CourseData } from "../../../../../types/course";
import { EducationalInstitution } from "../../../../../types/EducationalInstitution";
import { Category } from "../../../../../types/Category";
import {
  mockCertificates,
  mockCourseData,
  mockCourseDays,
  mockCourseLanguages,
  mockCourseLevels,
  mockCourseTimings,
  mockCourseTypes,
} from "./mockData";
import { DropdownOption } from "../../../../../types/common";

const AddEditCourse = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courseData, setCourseData] = useState<CourseData>(mockCourseData);
  const [courseCategories, setCourseCategories] = useState<Category[]>([]);
  const [courseDays, setCourseDays] =
    useState<DropdownOption[]>(mockCourseDays);
  const [courseLevels, setCourseLevels] = useState<DropdownOption[]>([]);
  const [courseTypes, setCourseTypes] = useState<DropdownOption[]>([]);
  const [courseTimings, setCourseTimings] = useState<DropdownOption[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [courseLanguages, setCourseLanguages] = useState<DropdownOption[]>([]);
  const [certificates, setCertificates] = useState<DropdownOption[]>([]);
  const [educationalInstitutions, setEducationalInstitutions] = useState<
    EducationalInstitution[]
  >([]);

  const [selectedInstitution, setSelectedInstitution] =
    useState<EducationalInstitution | null>(null);
  const [selectedMobileCode, setSelectedMobileCode] = useState("+963");
  const [selectedCountryCode, setSelectedCountryCode] = useState("+963");

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [brochurePreview, setBrochurePreview] = useState<string | null>(null);

  const [institutionSearch, setInstitutionSearch] = useState("");
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [institutionPage, setInstitutionPage] = useState(1);
  const [hasMoreInstitutions, setHasMoreInstitutions] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const theme = useTheme();

  const fetchCitiesByCountry = async (countryId: number) => {
    try {
      const response = await USERAXIOS.get(`/countries?parent_id=${countryId}`);
      if (response.data.data) {
        setCities(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching cities",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await USERAXIOS.get("/category?page=1");
        if (response.data.data) {
          setCourseCategories(response.data.data);
        }
      } catch (error) {
        openSnackbar({
          open: true,
          message: "Error fetching categories",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      }
    };

    fetchCategories();

    setCourseDays(mockCourseDays);

    setCourseLevels(mockCourseLevels);

    setCourseTypes(mockCourseTypes);

    setCourseTimings(mockCourseTimings);

    setCourseLanguages(mockCourseLanguages);

    setCertificates(mockCertificates);

    const fetchCountries = async () => {
      try {
        const response = await USERAXIOS.get("/countries?parent_id=");
        if (response.data.data) {
          setCountries(response.data.data);
        }
      } catch (error) {
        openSnackbar({
          open: true,
          message: "Error fetching countries",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      }
    };

    fetchCountries();

    if (id) {
      fetchCourseData();
    } else {
      fetchCitiesByCountry(19);
    }

    fetchEducationalInstitutions();
  }, [id]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const response = await USERAXIOS.get(`/courses/${id}`);
      const data = response.data.data;

      setSelectedMobileCode(data.mobile?.split("-")[0]);
      setSelectedCountryCode(data.phone?.split("-")[0]);
      setMobileNumber(data.mobile?.split("-")[1]);
      setPhoneNumber(data.phone?.split("-")[1]);

      if (data.country_id) {
        await fetchCitiesByCountry(data.country_id);
      }

      if (data.logo) {
        setLogoPreview(data.logo);
      }
      if (data.image) {
        setImagePreview(data.image);
      }
      if (data.brochure) {
        setBrochurePreview(data.brochure);
      }

      setCourseData({
        ...data,
        course_day_id: data.course_days || [],
        city_id: data.city_id || 0,
      });
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching course data",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (document.documentElement.dir !== "rtl") return;

    const flags = document.querySelectorAll(
      ".flag.sy"
    ) as NodeListOf<HTMLElement>;
    flags.forEach((flag) => {
      flag.style.right = "22px";
    });
  }, []);

  const handleSubmit = async (values: CourseData) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append(
        "course_category_id",
        values.course_category_id.toString()
      );
      formData.append("start_date", values.start_date);
      formData.append("end_date", values.end_date);
      formData.append("count_days", values.count_days.toString());
      formData.append("last_registration_date", values.last_registration_date);

      values.course_day_id.forEach((dayValue: string, index: number) => {
        const dayObj = courseDays.find((day) => day.value === dayValue);
        formData.append(
          `course_days[${index}]`,
          dayObj ? dayObj.name.toLowerCase() : dayValue.toLowerCase()
        );
      });

      formData.append("course_level", values.course_level);
      formData.append("course_type", values.course_type);
      formData.append("course_period", values.course_period);
      formData.append("country_id", values.country_id.toString());
      formData.append("city_id", values.city_id.toString());
      formData.append("address", values.address);
      formData.append("status", values.status ? "1" : "0");
      formData.append("course_lang", values.course_lang);
      formData.append("certificate", values.certificate);
      formData.append("tutor_name", values.tutor_name);
      formData.append("tutor_profile", values.tutor_profile);

      if (values.image && typeof values.image !== "string") {
        formData.append("image", values.image);
      }

      formData.append("benefit", values.benefit);
      formData.append("target_group", values.target_group);
      formData.append("discount", values.discount ? "1" : "0");
      formData.append("price", values.price.toString());
      formData.append("course_principle", values.course_principle);
      formData.append("max_std_num", values.max_std_num.toString());

      if (values.brochure && typeof values.brochure !== "string") {
        formData.append("brochure", values.brochure);
      }

      formData.append("email", values.email);
      formData.append("show_email", values.show_email ? "1" : "0");
      formData.append("mobile", selectedMobileCode + "-" + mobileNumber);
      formData.append("show_mobile", values.show_mobile ? "1" : "0");
      formData.append("phone", selectedCountryCode + "-" + phoneNumber);
      formData.append("show_phone", values.show_phone ? "1" : "0");
      formData.append("description", values.description);

      formData.append("company_id", "3");
      formData.append("added_benefit", values.benefit);

      if (values.logo && typeof values.logo !== "string") {
        formData.append("logo", values.logo);
      }
      if (id) {
        await USERAXIOS.post(`/courses/${id}`, formData);
      } else {
        await USERAXIOS.post("/courses", formData);
      }
      openSnackbar({
        open: true,
        message: `Course ${id ? "updated" : "created"} successfully`,
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "success",
        },
      } as SnackbarProps);

      navigate("/training-courses");
    } catch (error) {
      console.error("Error saving course:", error);
      openSnackbar({
        open: true,
        message: `Error ${id ? "updating" : "creating"} course`,
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue("image", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBrochureChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue("brochure", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBrochurePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue("logo", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchEducationalInstitutions = async (search = "", page = 1) => {
    if (selectedInstitution) return;

    try {
      setLoadingInstitutions(true);
      const response = await GUESTAXIOS.get(
        `/educationalInstitution?page=${page}`
      );
      if (response.data.data) {
        const institutionsData = response.data.data.map((institution: any) => ({
          id: institution.id,
          name: institution.name,
        }));

        if (page === 1) {
          setEducationalInstitutions(institutionsData);
        } else {
          setEducationalInstitutions((prev) => [...prev, ...institutionsData]);
        }

        setHasMoreInstitutions(response.data.data.length === 10);
      }
    } catch (error) {
      console.error("Error fetching educational institutions:", error);
      openSnackbar({
        open: true,
        message: "Error fetching educational institutions",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoadingInstitutions(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((search: string) => {
      if (!selectedInstitution) {
        setInstitutionPage(1);
        fetchEducationalInstitutions(search, 1);
      }
    }, 500),
    [selectedInstitution]
  );

  return (
    <Box sx={{ p: 3, backgroundColor: "background.default" }}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "primary",
        }}
        open={loading}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
        </Stack>
      </Backdrop>

      <Typography
        variant="h4"
        sx={{
          textAlign: "center",
          color: "primary.main",
          marginBottom: "3rem",
        }}
      >
        <FormattedMessage
          id={id ? "Edit Training Course" : "Add Training Course"}
        />
      </Typography>

      <Formik
        initialValues={courseData}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form>
            <MainCard
              style={{
                marginTop: "0px",
                marginBottom: "2rem",
              }}
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  <FormattedMessage id="Training Center" />
                </Typography>
              }
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field name="company_id">
                    {({ field }: { field: any }) => (
                      <Autocomplete
                        {...field}
                        disabled={!!id}
                        options={educationalInstitutions}
                        getOptionLabel={(option: EducationalInstitution) =>
                          option.name || ""
                        }
                        loading={loadingInstitutions}
                        value={selectedInstitution}
                        onChange={(_, newValue: EducationalInstitution) => {
                          setFieldValue("company_id", newValue?.id || 0);
                          setSelectedInstitution(newValue);
                          // Clear the search input when an item is selected
                          setInstitutionSearch("");
                        }}
                        onInputChange={(_, newInputValue) => {
                          setInstitutionSearch(newInputValue);
                          if (!selectedInstitution) {
                            debouncedSearch(newInputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search Training Center"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingInstitutions ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        ListboxProps={{
                          onScroll: (event: React.SyntheticEvent) => {
                            if (!selectedInstitution) {
                              const listboxNode = event.currentTarget;
                              if (
                                listboxNode.scrollTop +
                                  listboxNode.clientHeight >=
                                  listboxNode.scrollHeight - 1 &&
                                !loadingInstitutions &&
                                hasMoreInstitutions
                              ) {
                                setInstitutionPage((prev) => prev + 1);
                                fetchEducationalInstitutions(
                                  institutionSearch,
                                  institutionPage + 1
                                );
                              }
                            }
                          },
                        }}
                        // Add this to clear selection when input is cleared
                        onBlur={() => {
                          if (!field.value) {
                            setSelectedInstitution(null);
                          }
                        }}
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
            </MainCard>

            <MainCard
              style={{
                marginTop: "0px",
                marginBottom: "2rem",
              }}
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  <FormattedMessage id="Course Details" />
                </Typography>
              }
            >
              <Grid container spacing={3}>
                {/* Course Title */}
                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course Title" />{" "}
                  </Typography>
                  <Field name="title">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Course Title"
                        required
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course Category" />{" "}
                  </Typography>
                  <FormControl fullWidth>
                    <Field name="course_category_id">
                      {({ field }: { field: any }) => (
                        <Select
                          {...field}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <FormattedMessage id="Course Period" />;
                            }

                            const selectedOption = courseCategories.find(
                              (opt) => opt.id === selected
                            );
                            if (!selectedOption) return null;

                            return theme.direction === "rtl"
                              ? selectedOption.name_trans.ar
                              : selectedOption.name;
                          }}
                        >
                          {courseCategories.map((option: Category) => (
                            <MenuItem key={option.id} value={option.id}>
                              {theme.direction === "rtl"
                                ? option.name_trans.ar
                                : option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Start Date" />{" "}
                  </Typography>
                  <Field name="start_date">
                    {({ field, form }: { field: any; form: any }) => (
                      <DatePicker
                        style={{ width: "100%" }}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => {
                          form.setFieldValue(
                            field.name,
                            date ? date.format("YYYY-MM-DD") : null
                          );
                        }}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="End Date" />{" "}
                  </Typography>
                  <Field name="end_date">
                    {({ field, form }: { field: any; form: any }) => (
                      <DatePicker
                        style={{ width: "100%" }}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => {
                          form.setFieldValue(
                            field.name,
                            date ? date.format("YYYY-MM-DD") : null
                          );
                        }}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Number of Days" />{" "}
                  </Typography>
                  <Field name="count_days">
                    {({ field }: { field: any }) => (
                      <TextField {...field} fullWidth type="number" required />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Last Registration Date" />{" "}
                  </Typography>
                  <Field name="last_registration_date">
                    {({ field, form }: { field: any; form: any }) => (
                      <DatePicker
                        style={{ width: "100%" }}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => {
                          form.setFieldValue(
                            field.name,
                            date ? date.format("YYYY-MM-DD") : null
                          );
                        }}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course day" />{" "}
                  </Typography>
                  <Field name="course_day_id">
                    {({ field }: { field: any }) => (
                      <Autocomplete
                        multiple
                        id="course-day-select"
                        options={courseDays}
                        getOptionLabel={(option: DropdownOption) =>
                          theme.direction === "rtl"
                            ? option.arabic_name || option.name
                            : option.name
                        }
                        value={courseDays.filter((day) =>
                          (field.value || []).includes(day.value)
                        )}
                        onChange={(_, newValue) => {
                          const selectedValues = newValue.map(
                            (item) => item.value
                          );
                          setFieldValue("course_day_id", selectedValues);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select days (max 3)"
                            helperText="Maximum 3 days allowed"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.23)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "primary.main",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "primary.main",
                                },
                              },
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={
                                theme.direction === "rtl"
                                  ? option.arabic_name || option.name
                                  : option.name
                              }
                              {...getTagProps({ index })}
                              sx={{
                                backgroundColor: "primary.light",
                                color: "white",
                                "& .MuiChip-deleteIcon": {
                                  color: "white",
                                  "&:hover": {
                                    color: "error.main",
                                  },
                                },
                              }}
                            />
                          ))
                        }
                        disabled={false}
                        disableCloseOnSelect
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        getOptionDisabled={(option) =>
                          (field.value || []).length >= 3 &&
                          !(field.value || []).includes(option.value)
                        }
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course Level" />{" "}
                  </Typography>
                  <Field name="course_level">
                    {({ field }: { field: any }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <FormattedMessage id="Course Period" />;
                            }

                            const selectedOption = courseLevels.find(
                              (opt) => opt.value === selected
                            );
                            if (!selectedOption) return null;

                            return theme.direction === "rtl"
                              ? selectedOption.arabic_name
                              : selectedOption.name;
                          }}
                        >
                          {courseLevels.map((option) => (
                            <MenuItem key={option.id} value={option.value}>
                              {theme.direction === "rtl"
                                ? option.arabic_name
                                : option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course Type" />{" "}
                  </Typography>
                  <Field name="course_type">
                    {({ field }: { field: any }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <FormattedMessage id="Course Period" />;
                            }

                            const selectedOption = courseTypes.find(
                              (opt) => opt.value === selected
                            );
                            if (!selectedOption) return null;

                            return theme.direction === "rtl"
                              ? selectedOption.arabic_name
                              : selectedOption.name;
                          }}
                        >
                          {courseTypes.map((option) => (
                            <MenuItem key={option.id} value={option.value}>
                              {theme.direction === "rtl"
                                ? option.arabic_name
                                : option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course Period" />{" "}
                  </Typography>
                  <Field name="course_period">
                    {({ field }: { field: any }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <FormattedMessage id="Course Period" />;
                            }

                            const selectedOption = courseTimings.find(
                              (opt) => opt.value === selected
                            );
                            if (!selectedOption) return null;

                            return theme.direction === "rtl"
                              ? selectedOption.arabic_name
                              : selectedOption.name;
                          }}
                        >
                          {courseTimings.map((option) => (
                            <MenuItem key={option.id} value={option.value}>
                              {theme.direction === "rtl"
                                ? option.arabic_name
                                : option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course Language" />{" "}
                  </Typography>
                  <Field name="course_lang">
                    {({ field }: { field: any }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <FormattedMessage id="Course Period" />;
                            }

                            const selectedOption = courseLanguages.find(
                              (opt) => opt.value === selected
                            );
                            if (!selectedOption) return null;

                            return theme.direction === "rtl"
                              ? selectedOption.arabic_name
                              : selectedOption.name;
                          }}
                        >
                          {courseLanguages.map((option) => (
                            <MenuItem key={option.id} value={option.value}>
                              {theme.direction === "rtl"
                                ? option.arabic_name
                                : option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Certificate" />{" "}
                  </Typography>
                  <Field name="certificate">
                    {({ field }: { field: any }) => (
                      <FormControl fullWidth>
                        <Select
                          {...field}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <FormattedMessage id="Course Period" />;
                            }

                            const selectedOption = certificates.find(
                              (opt) => opt.value === selected
                            );
                            if (!selectedOption) return null;

                            return theme.direction === "rtl"
                              ? selectedOption.arabic_name
                              : selectedOption.name;
                          }}
                        >
                          {certificates.map((option) => (
                            <MenuItem key={option.id} value={option.value}>
                              {theme.direction === "rtl"
                                ? option.arabic_name
                                : option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Country" />{" "}
                  </Typography>
                  <Field name="country_id">
                    {({ field }: { field: any }) => (
                      <Autocomplete
                        id="country-select"
                        options={countries}
                        getOptionLabel={(option: Country) =>
                          theme.direction === "rtl"
                            ? option.name_trans.ar
                            : option.name
                        }
                        value={
                          countries.find(
                            (country) => country.id === field.value
                          ) || null
                        }
                        onChange={(_, newValue) => {
                          const selectedCountryId = newValue?.id || 0;
                          setFieldValue("country_id", selectedCountryId);
                          if (selectedCountryId) {
                            fetchCitiesByCountry(selectedCountryId);
                          } else {
                            setCities([]);
                          }
                          setFieldValue("city_id", 0);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select country"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.23)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "primary.main",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "primary.main",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Field>
                </Grid>

                {/* City */}
                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="City" />{" "}
                  </Typography>
                  <Field name="city_id">
                    {({ field }: { field: any }) => (
                      <Autocomplete
                        id="city-select"
                        options={cities}
                        getOptionLabel={(option: City) =>
                          theme.direction === "rtl"
                            ? option.name_trans.ar
                            : option.name
                        }
                        value={
                          cities.find((city) => city.id === field.value) || null
                        }
                        onChange={(_, newValue) => {
                          setFieldValue("city_id", newValue?.id || 0);
                        }}
                        disabled={!values.country_id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select city"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "rgba(0, 0, 0, 0.23)",
                                },
                                "&:hover fieldset": {
                                  borderColor: "primary.main",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "primary.main",
                                },
                              },
                            }}
                          />
                        )}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Address" />{" "}
                  </Typography>
                  <Field name="address">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Address"
                        multiline
                        rows={3}
                        required
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Logo" />
                  </Typography>
                  <ImageUploader
                    id="logo-upload"
                    previewUrl={logoPreview}
                    onChange={handleLogoChange}
                    setFieldValue={setFieldValue}
                    label="Click to upload logo"
                  />
                </Grid>
              </Grid>
            </MainCard>

            <MainCard
              style={{
                marginTop: "0px",
                marginBottom: "2rem",
              }}
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  <FormattedMessage id="About Tutor" />
                </Typography>
              }
            >
              <Grid container spacing={3}>
                {/* Tutor Name */}
                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Tutor Name" />{" "}
                  </Typography>
                  <Field name="tutor_name">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Tutor Name"
                        required
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Tutor Qualification" />{" "}
                  </Typography>
                  <Field name="tutor_profile">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Tutor Qualification"
                        multiline
                        rows={4}
                        required
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Tutor Image" />{" "}
                  </Typography>
                  <ImageUploader
                    id="tutor-image-upload"
                    previewUrl={imagePreview}
                    onChange={handleImageChange}
                    setFieldValue={setFieldValue}
                    label="Click to upload tutor image"
                  />
                </Grid>
              </Grid>
            </MainCard>

            <MainCard
              style={{
                marginTop: "0px",
                marginBottom: "2rem",
              }}
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  <FormattedMessage id="Additional Information" />
                </Typography>
              }
            >
              <Grid container spacing={3}>
                {" "}
                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Course Benefit" />{" "}
                  </Typography>
                  <Field name="benefit">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Course Benefit"
                        multiline
                        rows={4}
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Target Group" />{" "}
                  </Typography>
                  <Field name="target_group">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Target Group"
                        multiline
                        rows={4}
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Course Price" />{" "}
                  </Typography>
                  <Field name="price">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        placeholder="Course Price"
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography>
                    <FormattedMessage id="Maximum Students Number" />{" "}
                  </Typography>
                  <Field name="max_std_num">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        placeholder="Maximum Students Number"
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Course Principle" />{" "}
                  </Typography>
                  <Field name="course_principle">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Course Principle"
                        multiline
                        rows={4}
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Description" />{" "}
                  </Typography>
                  <Field name="description">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Description"
                        multiline
                        rows={4}
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <FormattedMessage id="Course Brochure" />{" "}
                  </Typography>

                  <ImageUploader
                    id="brochure-upload"
                    previewUrl={brochurePreview}
                    onChange={handleBrochureChange}
                    setFieldValue={setFieldValue}
                    label="Click to upload course brochure"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.discount}
                        onChange={(e) => {
                          setFieldValue("discount", e.target.checked);
                        }}
                      />
                    }
                    label={<FormattedMessage id="Discount" />}
                  />
                </Grid>
              </Grid>
            </MainCard>

            <MainCard
              style={{
                marginTop: "0px",
                marginBottom: "2rem",
              }}
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  <FormattedMessage id="Contact Information" />
                </Typography>
              }
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography>
                        <FormattedMessage id="Mobile" />{" "}
                      </Typography>
                      <Box sx={{ display: "flex" }}>
                        <PhoneInput
                          country={"sy"}
                          value={selectedMobileCode}
                          onChange={(phone) => {
                            setSelectedMobileCode(
                              phone.startsWith("+") ? phone : `+${phone}`
                            );
                          }}
                          inputStyle={{
                            width: "90px",
                            height: "56px",
                            fontSize: "16px",
                            borderRadius: "4px",
                          }}
                          containerStyle={{
                            width: "41px",
                            position: "relative",
                          }}
                          buttonStyle={{
                            borderRadius:
                              theme.direction === "rtl"
                                ? "0 4px 4px 0"
                                : "4px 0 0 4px",
                          }}
                          dropdownStyle={{
                            width: "300px",
                            maxHeight: "300px",
                            overflow: "auto",
                            zIndex: 9999,
                          }}
                          searchPlaceholder="Search country..."
                          searchNotFound="Country not found"
                          enableSearch={true}
                          disableSearchIcon={true}
                          searchStyle={{
                            width: "90%",
                            margin: "0 auto",
                            marginBottom: "10px",
                          }}
                          inputProps={{
                            style: { display: "none" },
                          }}
                          countryCodeEditable={false}
                        />
                        <TextField
                          fullWidth
                          type="tel"
                          placeholder="Mobile Number"
                          value={mobileNumber}
                          onChange={(e) => {
                            const numbersOnly = e.target.value.replace(
                              /[^\d]/g,
                              ""
                            );
                            setMobileNumber(numbersOnly);
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "56px",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.show_mobile}
                            onChange={(e) => {
                              setFieldValue("show_mobile", e.target.checked);
                            }}
                          />
                        }
                        label={<FormattedMessage id="Show Mobile" />}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography>
                        <FormattedMessage id="Phone" />
                      </Typography>
                      <Box sx={{ display: "flex" }}>
                        <PhoneInput
                          country={"sy"}
                          value={selectedCountryCode}
                          onChange={(phone) => {
                            setSelectedCountryCode(
                              phone.startsWith("+") ? phone : `+${phone}`
                            );
                          }}
                          inputStyle={{
                            width: "90px",
                            height: "56px",
                            fontSize: "16px",
                          }}
                          containerStyle={{
                            width: "41px",
                            position: "relative",
                          }}
                          buttonStyle={{
                            borderRadius:
                              theme.direction === "rtl"
                                ? "0 4px 4px 0"
                                : "4px 0 0 4px",
                          }}
                          dropdownStyle={{
                            width: "300px",
                            maxHeight: "300px",
                            overflow: "auto",
                            zIndex: 9999,
                          }}
                          searchPlaceholder="Search country..."
                          searchNotFound="Country not found"
                          enableSearch={true}
                          disableSearchIcon={true}
                          searchStyle={{
                            width: "90%",
                            margin: "0 auto",
                            marginBottom: "10px",
                          }}
                          inputProps={{
                            style: { display: "none" },
                          }}
                          countryCodeEditable={false}
                        />
                        <TextField
                          fullWidth
                          type="tel"
                          placeholder="Phone Number"
                          value={phoneNumber}
                          onChange={(e) => {
                            const numbersOnly = e.target.value.replace(
                              /[^\d]/g,
                              ""
                            );
                            setPhoneNumber(numbersOnly);
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "56px",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.show_phone}
                            onChange={(e) => {
                              setFieldValue("show_phone", e.target.checked);
                            }}
                          />
                        }
                        label={<FormattedMessage id="Show Phone" />}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography>
                        <FormattedMessage id="Email" />{" "}
                      </Typography>
                      <Field name="email">
                        {({ field }: { field: any }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="email"
                            placeholder="Email"
                            required
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.show_email}
                            onChange={(e) => {
                              setFieldValue("show_email", e.target.checked);
                            }}
                          />
                        }
                        label={<FormattedMessage id="Show Email" />}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.status}
                        onChange={(e) => {
                          setFieldValue("status", e.target.checked);
                          setCourseData({
                            ...values,
                            status: e.target.checked,
                          });
                        }}
                      />
                    }
                    label={<FormattedMessage id="Status" />}
                  />
                </Grid>
              </Grid>
            </MainCard>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              marginTop="2rem"
            >
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate("/training-courses")}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress
                          size="1.5rem"
                          style={{ color: "white" }}
                        />
                      ) : (
                        <FormattedMessage id={id ? "Update" : "Create"} />
                      )}
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AddEditCourse;
